import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, Trash2, Loader2, X, Save, UserCog } from "lucide-react";

export default function AdminUsers() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    setSaving(true);
    try {
      await api.post("/admin/users", { ...form, email: form.email.trim().toLowerCase() });
      toast.success("Admin user created.");
      setAdding(false);
      setForm({ email: "", password: "", name: "" });
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const remove = async (u) => {
    if (u.id === me?.id) { toast.error("You cannot delete your own account."); return; }
    if (!window.confirm(`Delete ${u.email}?`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("Removed.");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="admin-users">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
        <div>
          <h1 className="font-display text-3xl text-[var(--rts-ink)]">Admin Users</h1>
          <p className="text-sm text-[var(--rts-muted)] mt-1">Anyone here can sign in and manage site content.</p>
        </div>
        <button
          onClick={() => setAdding(true)}
          data-testid="users-new-btn"
          className="inline-flex items-center gap-2 bg-[var(--rts-blue-deep)] hover:bg-[var(--rts-blue)] text-white font-bold px-5 py-2.5 rounded-full min-h-[44px]"
        >
          <Plus className="w-4 h-4" /> New Admin
        </button>
      </div>

      {loading ? (
        <div className="py-16 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-[var(--rts-blue-deep)]" /></div>
      ) : (
        <div className="bg-white rounded-2xl border border-[var(--rts-blue-pale)] divide-y divide-[var(--rts-blue-pale)]">
          {users.map((u) => (
            <div key={u.id} className="p-4 flex items-center gap-3" data-testid={`user-row-${u.email}`}>
              <div className="w-11 h-11 rounded-xl bg-[var(--rts-blue-pale)] grid place-items-center text-[var(--rts-blue-deep)]">
                <UserCog className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-[var(--rts-ink)] truncate">{u.name || u.email}</div>
                <div className="text-xs text-[var(--rts-muted)] truncate">{u.email}{u.id === me?.id ? " · you" : ""}</div>
              </div>
              {u.id !== me?.id && (
                <button onClick={() => remove(u)} aria-label="Delete" className="w-10 h-10 grid place-items-center rounded-lg bg-red-50 hover:bg-[var(--rts-red)] hover:text-white text-[var(--rts-red)]" data-testid={`user-delete-${u.email}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {adding && (
        <div className="fixed inset-0 z-[80] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b">
              <h2 className="font-display text-xl">New admin</h2>
              <button onClick={() => setAdding(false)} className="w-10 h-10 grid place-items-center rounded-lg hover:bg-[var(--rts-blue-pale)]"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <Field label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="users-field-name" />
              <Field label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="users-field-email" />
              <Field label="Password (min 8 chars)" type="password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} testid="users-field-password" />
            </div>
            <div className="p-5 border-t flex justify-end gap-3">
              <button onClick={() => setAdding(false)} className="px-5 py-2.5 rounded-full font-semibold hover:bg-[var(--rts-blue-pale)] min-h-[44px]">Cancel</button>
              <button onClick={create} disabled={saving} data-testid="users-save-btn" className="inline-flex items-center gap-2 bg-[var(--rts-blue-deep)] hover:bg-[var(--rts-blue)] text-white font-bold px-6 py-2.5 rounded-full disabled:opacity-60 min-h-[44px]">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Create
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <label className="block">
      <span className="block text-sm font-semibold text-[var(--rts-ink)] mb-1">{label}</span>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="w-full border-2 border-[var(--rts-blue-pale)] focus:border-[var(--rts-blue)] rounded-xl px-3 py-2.5 outline-none min-h-[44px]" />
    </label>
  );
}
